import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@constants/colors';
import { WatchlistRow } from '@components/watchlist/WatchlistRow';
import { useWatchlist } from '@hooks/useWatchlist';
import { useMarketData } from '@hooks/useMarketData';
import { getFullStockConversationHistory } from '@services/history/searchHistoryService';
import { stockDataService } from '@services/api/stockData.service';

interface WatchlistScreenProps {
  navigation: any;
}

export const WatchlistScreen: React.FC<WatchlistScreenProps> = ({ navigation }) => {
  const { watchlist, addToWatchlist, removeFromWatchlist, isInWatchlist } = useWatchlist();
  const { marketData } = useMarketData();

  const handleStockPress = async (ticker: string, name: string) => {
    // Load history for this stock (same logic as HomeScreen)
    const [historyResult, stockData] = await Promise.all([
      getFullStockConversationHistory(ticker),
      stockDataService.getStockData(ticker),
    ]);

    if (historyResult.success && historyResult.messages && historyResult.messages.length > 0) {
      // Navigate with full history
      navigation.navigate('StockChat', {
        ticker,
        companyName: name,
        previousMessages: historyResult.messages,
        stockData: stockData,
      });
    } else {
      // No history, navigate normally
      navigation.navigate('StockChat', {
        ticker,
        companyName: name,
        stockData: stockData,
      });
    }
  };

  const handleWatchlistToggle = async (ticker: string, companyName: string) => {
    if (isInWatchlist(ticker)) {
      await removeFromWatchlist(ticker);
    } else {
      await addToWatchlist(ticker, companyName);
    }
  };

  const handleBack = () => {
    navigation.goBack();
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" />

      {/* Header */}
      <View style={styles.header}>
        <Pressable onPress={handleBack} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={Colors.text.primary} />
        </Pressable>
        <Text style={styles.headerTitle}>My Watchlist</Text>
        <View style={styles.headerRight} />
      </View>

      {/* Watchlist */}
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {watchlist.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons
              name="star-outline"
              size={64}
              color={Colors.text.tertiary}
              style={{ opacity: 0.4 }}
            />
            <Text style={styles.emptyTitle}>No stocks in watchlist</Text>
            <Text style={styles.emptySubtitle}>
              Star stocks from the home screen to track them here
            </Text>
          </View>
        ) : (
          watchlist.map(item => {
            const liveData = marketData.find(m => m.symbol === item.ticker);
            return (
              <WatchlistRow
                key={item.id}
                symbol={item.ticker}
                name={item.companyName}
                price={liveData?.price ?? null}
                changePercent={liveData?.changePercent ?? 0}
                isInWatchlist={true}
                onPress={() => handleStockPress(item.ticker, item.companyName)}
                onWatchlistToggle={() => handleWatchlistToggle(item.ticker, item.companyName)}
              />
            );
          })
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  headerRight: {
    width: 32, // Balance layout
  },
  scrollView: {
    flex: 1,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
    paddingTop: 120,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
    marginTop: 24,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: Colors.text.tertiary,
    textAlign: 'center',
    lineHeight: 20,
  },
});
